import { Input } from 'commons/components';
import { PocAssistiveText, PocCheckbox, PocIcon, PocTypography } from 'commons/components/components-wrapper';
import { useTranslation } from 'core/i18n/i18n';
import {
    FormSection,
    FormSectionHint,
    FormSectionTitle,
    ThresholdRow,
} from 'modules/settings/components/custom-notifications/custom-notification-form.style';
import {
    MAXIMUM_OCCURRENCES,
    MINIMUM_OCCURRENCES,
} from 'modules/settings/components/custom-notifications/custom-notification-form.constants';
import { preventNonDigitKey } from 'modules/settings/components/custom-notifications/custom-notification-form.utils';
import {
    getThresholdErrorKeys,
    getThresholdValidationMessages,
    getThresholdValidity,
} from 'modules/settings/components/custom-notifications/custom-notification-form.validation';
import {
    NOTIFICATION_LEVELS,
    NotificationLevel,
    NotificationThresholds,
} from 'modules/settings/components/custom-notifications/custom-notification-threshold.model';
import {
    THRESHOLD_LABEL_KEYS,
    THRESHOLD_OCCURRENCES_ARIA_KEY,
    THRESHOLDS_HINT_KEY,
    THRESHOLDS_TITLE_KEY,
} from 'modules/settings/components/custom-notifications/custom-notification-thresholds-section/custom-notification-thresholds-section.constants';

export interface CustomNotificationThresholdsSectionProps {
    notifications: NotificationThresholds;
    submitAttempted?: boolean;
    thresholdsTouched?: boolean;
    onEnabledChange: (level: NotificationLevel, enabled: boolean) => void;
    onNumberOfEventsChange: (level: NotificationLevel, numberOfEvents: string | null) => void;
}

const thresholdIcon = (level: NotificationLevel) => {
    if (level === 'gray') {
        return <PocIcon name="circle_info" />;
    }

    return <PocIcon family="filled" name="alarm" status={level === 'yellow' ? 'warning' : 'alarm'} />;
};

export const CustomNotificationThresholdsSection: React.FC<CustomNotificationThresholdsSectionProps> = ({
    notifications,
    submitAttempted = false,
    thresholdsTouched = false,
    onEnabledChange,
    onNumberOfEventsChange,
}) => {
    const { t } = useTranslation();

    const validationMessages = getThresholdValidationMessages(t);
    const errorKeys = getThresholdErrorKeys(notifications, submitAttempted, thresholdsTouched);

    return (
        <FormSection>
            <FormSectionTitle variant="subtitle2">{t(THRESHOLDS_TITLE_KEY)}</FormSectionTitle>
            <FormSectionHint variant="body2">{t(THRESHOLDS_HINT_KEY)}</FormSectionHint>

            {NOTIFICATION_LEVELS.map((level) => (
                <ThresholdRow key={level}>
                    <PocCheckbox
                        aria-label={t(THRESHOLD_LABEL_KEYS[level])}
                        checked={notifications[level].enabled}
                        onValueChange={(event) => onEnabledChange(level, event.detail)}
                    />
                    {thresholdIcon(level)}
                    <PocTypography variant="body1">{t(THRESHOLD_LABEL_KEYS[level])}</PocTypography>
                    <Input
                        id={`${level}Events`}
                        name={`${level}Events`}
                        ariaLabel={t(THRESHOLD_OCCURRENCES_ARIA_KEY, { threshold: t(THRESHOLD_LABEL_KEYS[level]) })}
                        placeholder=""
                        type="number"
                        minValue={MINIMUM_OCCURRENCES}
                        maxValue={MAXIMUM_OCCURRENCES}
                        value={notifications[level].numberOfEvents ?? ''}
                        disabled={!notifications[level].enabled}
                        validity={getThresholdValidity(notifications, level, submitAttempted)}
                        validationMessages={validationMessages}
                        onKeyDown={preventNonDigitKey}
                        onChange={(_, value) => onNumberOfEventsChange(level, value)}
                    />
                </ThresholdRow>
            ))}

            {errorKeys.map((errorKey) => (
                <PocAssistiveText key={errorKey} role="alert" status="alarm" wrap>
                    {t(errorKey)}
                </PocAssistiveText>
            ))}
        </FormSection>
    );
};
